import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { serializeHtmlCharacterEntitiesFunction } from '../../../functions/html-character-entities.function';
import { I18nModule } from '../../../i18n/i18n.module';
import { VirtualKeyboardDirective } from '../../../keyboard/directives/virtual-keyboard.directive';
import { UiCorePipesModule } from '../../../pipes/ui-core-pipes.module';

@Component({
  selector: 'soft-multiline-input',
  imports: [CommonModule, UiCorePipesModule, I18nModule],
  templateUrl: './multiline-input.component.html',
  styleUrls: ['./multiline-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultilineInputComponent),
      multi: true,
    },
  ],
  hostDirectives: [
    {
      directive: VirtualKeyboardDirective,
      inputs: ['softVirtualKeyboardEnabled', 'softVirtualKeyboardTrigger'],
      outputs: [
        'softVirtualKeyboardEnter',
        'softVirtualKeyboardAppear',
        'softVirtualKeyboardDisappear',
        'softVirtualKeyboardBackdropClick',
        'softVirtualKeyboardInput',
        'softVirtualKeyboardChange:softVirtualKeyboardValueChange',
      ],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultilineInputComponent {
  valueLines = 1;

  private directive = inject(VirtualKeyboardDirective);

  private onChange: Function = () => {};
  onTouch: Function = () => {};

  readonly _value = signal<string | null | undefined>(undefined);

  @Input()
  get value() {
    return this._value();
  }
  set value(value: string | null | undefined) {
    this._value.set(value);
  }

  @Output() valueChange: EventEmitter<string | null | undefined> = new EventEmitter<
    string | null | undefined
  >();

  @Input() placeholder?: string | null;
  @Input() @HostBinding('class.readonly') readonly = false;
  @Input() customClass = '';
  @Input() escapeHtml = false;

  @Input() canResize = false;
  @Input() lines: number | 'auto' = 'auto';
  @Input() maxLines: number = Number.POSITIVE_INFINITY;

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  readonly elementRef = inject(ElementRef);

  constructor() {
    inject(VirtualKeyboardDirective).register({
      element: this.inputElement,
      value: computed(() => this._value() ?? null),
      valueChange: (value) => this.setValue(value),
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  setValue(value: string | null): void {
    if (value && this.escapeHtml) value = serializeHtmlCharacterEntitiesFunction(value);
    this.value = value;

    if (this.lines === 'auto')
      this.valueLines = Math.min(value?.split(/\r\n|\r|\n/).length ?? 0, this.maxLines);

    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  @ViewChild('inputElement') inputRef!: ElementRef<HTMLTextAreaElement>;

  focusAndSelect(): void {
    const input = this.inputRef?.nativeElement;
    input.focus();
    input.select();
  }
}
